import { toLocalDate } from "../../domain/time";
import type { EpochSeconds, LocalDate } from "../../domain/time";

/** A local date `YYYY-MM-DD`. */
const localDatePattern = /^\d{4}-\d{2}-\d{2}$/u;

/** A month `YYYY-MM`. */
const monthPattern = /^\d{4}-\d{2}$/u;

/**
 * Formats the local time of an instant (`08:05`).
 *
 * @param seconds - Epoch seconds.
 * @param languageTag - BCP 47 tag.
 * @param timeZone - IANA time zone.
 * @returns The formatted time.
 */
export function formatTime(seconds: EpochSeconds, languageTag: string, timeZone: string): string {
  const formatted = new Intl.DateTimeFormat(languageTag, { timeStyle: "short", timeZone }).format(
    seconds * 1000,
  );

  return formatted;
}

/**
 * Formats date and time of an instant with a four-digit year (`25.09.2026, 09:00`; C-3, UX-116).
 *
 * @param seconds - Epoch seconds.
 * @param languageTag - BCP 47 tag.
 * @param timeZone - IANA time zone.
 * @returns The formatted date and time.
 */
export function formatDateTime(
  seconds: EpochSeconds,
  languageTag: string,
  timeZone: string,
): string {
  const formatted = new Intl.DateTimeFormat(languageTag, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(seconds * 1000);

  return formatted;
}

/**
 * Formats an instant as its time if it falls on today, otherwise with its date (`23:58` on the
 * same day, `04.10.2026, 23:58` after midnight), so a time of another day is not read as one of
 * today (F-17).
 *
 * @param seconds - Epoch seconds.
 * @param today - Local date of today in `timeZone`.
 * @param languageTag - BCP 47 tag.
 * @param timeZone - IANA time zone of the display and of `today`.
 * @returns The formatted time, or date and time.
 */
export function formatTimeOrDateTime(
  seconds: EpochSeconds,
  today: LocalDate,
  languageTag: string,
  timeZone: string,
): string {
  const formatted =
    toLocalDate(seconds, timeZone) === today
      ? formatTime(seconds, languageTag, timeZone)
      : formatDateTime(seconds, languageTag, timeZone);

  return formatted;
}

/**
 * Formats a local date with weekday (`Do., 24.09.2026`).
 *
 * @param date - Local date.
 * @param languageTag - BCP 47 tag.
 * @returns The formatted date.
 */
export function formatLocalDate(date: LocalDate, languageTag: string): string {
  const formatted = new Intl.DateTimeFormat(languageTag, {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));

  return formatted;
}

/**
 * Formats a local date without weekday, for example a validity or a due date (`01.09.2026`
 * instead of `2026-09-01`; C-3, UX-116). A text that is no date is returned unchanged.
 *
 * @param date - Local date `YYYY-MM-DD`.
 * @param languageTag - BCP 47 tag.
 * @returns The formatted date.
 */
export function formatDate(date: LocalDate, languageTag: string): string {
  const formatted = localDatePattern.test(date)
    ? new Intl.DateTimeFormat(languageTag, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(`${date}T00:00:00Z`))
    : date;

  return formatted;
}

/**
 * Formats a month (`August 2026` instead of `2026-08`; C-3, UX-116). A text that is no month is
 * returned unchanged.
 *
 * @param month - Month `YYYY-MM`.
 * @param languageTag - BCP 47 tag.
 * @returns The formatted month.
 */
export function formatMonth(month: string, languageTag: string): string {
  const formatted = monthPattern.test(month)
    ? new Intl.DateTimeFormat(languageTag, {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      }).format(new Date(`${month}-01T00:00:00Z`))
    : month;

  return formatted;
}

/**
 * Formats a number in the language (`1.234,5` in German).
 *
 * @param value - The number.
 * @param languageTag - BCP 47 tag.
 * @returns The formatted number.
 */
export function formatNumber(value: number, languageTag: string): string {
  const formatted = new Intl.NumberFormat(languageTag).format(value);

  return formatted;
}

/**
 * Instant at which time zones are named (1 January 2025). The generic name follows the rules in
 * force at the instant: in 1970 Germany had no summer time, so „Mitteleuropäische Normalzeit“ was
 * shown all year, also in summer (F-57).
 */
const timeZoneNameReferenceMs = Date.UTC(2025, 0, 1);

/**
 * Names an IANA time zone in the language, with its identifier for reference
 * (`Mitteleuropäische Zeit (Europe/Berlin)`; UX-116). An unknown zone is returned unchanged.
 *
 * @param timeZone - IANA time zone.
 * @param languageTag - BCP 47 tag.
 * @returns The name.
 */
export function formatTimeZone(timeZone: string, languageTag: string): string {
  let name: string | undefined;

  try {
    name = new Intl.DateTimeFormat(languageTag, { timeZone, timeZoneName: "longGeneric" })
      .formatToParts(timeZoneNameReferenceMs)
      .find((part) => part.type === "timeZoneName")?.value;
  } catch {
    name = undefined;
  }

  const formatted = name === undefined || name === timeZone ? timeZone : `${name} (${timeZone})`;

  return formatted;
}

/**
 * Names a country by its ISO 3166 code in the language (`Deutschland` for `DE`). An unknown code
 * is returned unchanged.
 *
 * @param code - ISO 3166-1 alpha-2 code.
 * @param languageTag - BCP 47 tag.
 * @returns The name.
 */
export function formatCountry(code: string, languageTag: string): string {
  let name: string | undefined;

  try {
    name = new Intl.DisplayNames([languageTag], { type: "region", fallback: "none" }).of(
      code.toUpperCase(),
    );
  } catch {
    name = undefined;
  }

  const formatted = name ?? code;

  return formatted;
}

/**
 * Formats an amount in euro cents (`58,31 €`).
 *
 * @param cents - The amount in cents.
 * @param languageTag - BCP 47 tag.
 * @returns The formatted amount.
 */
export function formatEuroCents(cents: number, languageTag: string): string {
  const formatted = new Intl.NumberFormat(languageTag, {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);

  return formatted;
}
